import React, { MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import {
  IngestionResourceAction,
  IngestionResourceStatus,
  IngestionStageType,
  OperationProblem,
} from '../../../../../../gamma-api';
import { getStageProgress } from '../../utils';
import StatusInfo from '../../../../../components/common/StatusInfo';
import { StatusInfoState } from '../../../../../components/common/StatusInfo/types';

import './index.less';

import { MAIN_CLASS_NAME } from './const';

const DEFAULT_ITEM = {
  title: '',
  subtitle: '',
  description: '',
  problem: undefined as OperationProblem | null | undefined,
  modal: false,
};

export interface IResourceStatusProps {
  loading: boolean;
  rolling: boolean;
  direction?: 'horizontal' | 'vertical';
  status?: IngestionResourceStatus;
  stage?: IngestionStageType | null;
  action?: IngestionResourceAction;
  problem?: OperationProblem | null;
  onClick?: MouseEventHandler<HTMLElement>;
  onRestart?: () => void;
}

const ResourceStatus: React.FC<IResourceStatusProps> = ({
  loading,
  rolling,
  direction,
  status,
  stage,
  action,
  problem,
  onClick,
  onRestart,
}) => {
  rolling = loading || !stage || rolling;

  const { t } = useTranslation();

  const onStepsRestart = () => {
    !rolling && onRestart?.();
  };

  const getCurrent = (): number => {
    if (!status) {
      return 0;
    }

    if (stage) {
      return getStageProgress(stage);
    }

    if (status === IngestionResourceStatus.Pending || status === IngestionResourceStatus.Review) {
      return 0;
    }

    return getStageProgress(undefined);
  };

  const getStatus = (): StatusInfoState => {
    if (!status) {
      return StatusInfoState.Wait;
    }

    if (status === IngestionResourceStatus.Pending || status === IngestionResourceStatus.Review) {
      return StatusInfoState.Wait;
    }

    if (status === IngestionResourceStatus.Error || status === IngestionResourceStatus.Failure) {
      return StatusInfoState.Error;
    }

    if (stage) {
      return StatusInfoState.Process;
    }

    return StatusInfoState.Finish;
  };

  const items =
    action === IngestionResourceAction.Delete
      ? [
          { ...DEFAULT_ITEM, title: t('Resource stage: Deletion: Loading') },
          { ...DEFAULT_ITEM, title: t('Resource stage: Deletion: Extraction') },
          { ...DEFAULT_ITEM, title: t('Resource stage: Deletion: Parsing') },
          { ...DEFAULT_ITEM, title: t('Resource stage: Deletion: Embedding') },
          { ...DEFAULT_ITEM, title: t('Resource stage: Deletion: Indexing') },
          { ...DEFAULT_ITEM, title: t('Resource stage: Deletion: Deleted') },
        ]
      : [
          { ...DEFAULT_ITEM, title: t('Resource stage: Ingestion: Loading') },
          { ...DEFAULT_ITEM, title: t('Resource stage: Ingestion: Extraction') },
          { ...DEFAULT_ITEM, title: t('Resource stage: Ingestion: Parsing') },
          { ...DEFAULT_ITEM, title: t('Resource stage: Ingestion: Embedding') },
          { ...DEFAULT_ITEM, title: t('Resource stage: Ingestion: Indexing') },
          { ...DEFAULT_ITEM, title: t('Resource stage: Ingestion: Ingested') },
        ];

  const itemIndex = getCurrent();
  const itemStatus = getStatus();

  if (itemStatus === StatusInfoState.Error) {
    const item = items[itemIndex];
    item.subtitle = t('Resource stage: Problem: Subtitle');
    item.description = t('Resource stage: Problem: Description');
    item.problem = problem;
    item.modal = true;
  }

  if (direction !== 'vertical') {
    return (
      <StatusInfo.Steps
        className={MAIN_CLASS_NAME}
        current={itemIndex}
        direction="horizontal"
        items={items}
        rolling={rolling}
        status={itemStatus}
        onRestart={onStepsRestart}
      />
    );
  }

  const onDropdownClick = (e: React.MouseEvent<HTMLElement>) => {
    onClick?.(e);
  };

  return (
    <StatusInfo.Dropdown
      className={MAIN_CLASS_NAME}
      current={getCurrent()}
      items={items}
      rolling={rolling}
      status={getStatus()}
      onClick={onDropdownClick}
    />
  );
};

export default ResourceStatus;
